import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Card, CardContent, Typography, TextField, MenuItem, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Stack } from '@mui/material';
import { Link } from 'react-router-dom';
import apiService from '../../services/apiServices';
import PageHeader from '../common/PageHeader';
import { printReport } from '../../utils/pdfPrint';
import SendReportEmailDialog from '../common/SendReportEmailDialog';
import { ReportPage, ExportMenu } from './ReportKit';

function formatMinutes(minutes) {
  if (minutes === null || minutes === undefined || minutes === '') return '-';
  const total = Number(minutes);
  if (!Number.isFinite(total)) return '-';
  const rounded = Math.max(0, Math.round(total));
  const hrs = Math.floor(rounded / 60);
  const mins = rounded % 60;
  if (!hrs && !mins) return '0m';
  return `${hrs ? `${hrs}h ` : ''}${mins}m`.trim();
}

export default function ProjectActivityReport() {
  const { t } = useTranslation();

  const toCSVHeaders = () => [t('report.client'), t('report.project'), t('report.worker'), t('report.entries'), t('report.totalMinutes')];

  const toCSVRows = (rows) => {
    const headers = toCSVHeaders();
    const lines = [headers.join(',')];
    rows.forEach((r) => {
      const v = [
        r.client_name || '',
        r.project_name || '',
        r.user_name || '',
        r.entries || 0,
        r.total_minutes || 0,
      ];
      lines.push(v.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(','));
    });
    return lines.join('\n');
  };
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [clientId, setClientId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [userId, setUserId] = useState('');
  const [emailOpen, setEmailOpen] = useState(false);

  const pdfCols = [
    { label: t('report.client'), key: 'client_name', align: 'left', width: '25%' },
    { label: t('report.project'), key: 'project_name', align: 'left', width: '25%' },
    { label: t('report.worker'), key: 'user_name', align: 'left', width: '25%' },
    { label: t('report.entries'), key: 'entries', align: 'right', width: '10%' },
    { label: t('report.totalTime'), key: r => formatMinutes(r.total_minutes), align: 'right', width: '15%' },
  ];

  useEffect(() => {
    loadFilters();
    loadReport();
  }, []);

  const loadFilters = async () => {
    try {
      const [clientsRes, projectsRes, usersRes] = await Promise.all([
        apiService().get('clients'),
        apiService().get('projects'),
        apiService().get('cms_users'),
      ]);
      setClients(clientsRes.data || []);
      setProjects(projectsRes.data || []);
      setUsers(usersRes.data || []);
    } catch (_) {
      setClients([]);
      setProjects([]);
      setUsers([]);
    }
  };

  const loadReport = async () => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (clientId) params.set('client_id', clientId);
    if (projectId) params.set('project_id', projectId);
    if (userId) params.set('user_id', userId);
    try {
      const res = await apiService().query('projectActivityReport', params.toString());
      setRows(res?.data || []);
    } catch (_) {
      setRows([]);
    }
  };

  const filteredProjects = useMemo(() => {
    if (!clientId) return projects;
    return projects.filter((p) => String(p.client_id) === String(clientId));
  }, [projects, clientId]);

  const totalMinutes = useMemo(
    () => rows.reduce((sum, r) => sum + Number(r.total_minutes || 0), 0),
    [rows]
  );

  const exportPdf = () => printReport({
    title: t('reports.activities'),
    subtitle: from && to ? `${from} → ${to}` : new Date().toLocaleDateString('sq-AL'),
    orientation: 'landscape',
    columns: pdfCols,
    rows,
  });

  const handleExport = () => {
    const csv = toCSVRows(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'project_activity_report.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <ReportPage>
      <PageHeader title={t('reports.activities')} actions={<Button component={Link} to="/home/reports" variant="outlined">{t('report.backLabel')}</Button>} />
      <Card>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>{t('report.filters')}</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              type="date"
              label={t('report.from')}
              InputLabelProps={{ shrink: true }}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
            <TextField
              size="small"
              type="date"
              label={t('report.to')}
              InputLabelProps={{ shrink: true }}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
            <TextField
              select
              size="small"
              label={t('report.client')}
              value={clientId}
              onChange={(e) => {
                setClientId(e.target.value);
                setProjectId('');
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">{t('report.all')}</MenuItem>
              {clients.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label={t('report.project')}
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">{t('report.all')}</MenuItem>
              {filteredProjects.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label={t('report.worker')}
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">{t('report.all')}</MenuItem>
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {(u.name || '').trim()} {(u.surname || '').trim()}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ flex: 1 }} />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={loadReport}>{t('report.filterBtn')}</Button>
              <ExportMenu onCsv={handleExport} onPdf={exportPdf} onEmail={() => setEmailOpen(true)} />
            </Stack>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('report.total')}: {formatMinutes(totalMinutes)}
          </Typography>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('report.client')}</TableCell>
                  <TableCell>{t('report.project')}</TableCell>
                  <TableCell>{t('report.worker')}</TableCell>
                  <TableCell>{t('report.entries')}</TableCell>
                  <TableCell>{t('report.totalTime')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, idx) => (
                  <TableRow key={`${r.project_id || 'p'}-${r.user_id || 'u'}-${idx}`}>
                    <TableCell>{r.client_name || '-'}</TableCell>
                    <TableCell>{r.project_name || '-'}</TableCell>
                    <TableCell>{r.user_name || '-'}</TableCell>
                    <TableCell>{r.entries || 0}</TableCell>
                    <TableCell>{formatMinutes(r.total_minutes)}</TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary">{t('report.noData')}</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      <SendReportEmailDialog
        open={emailOpen} onClose={() => setEmailOpen(false)}
        reportTitle={t('reports.activities')}
        subtitle={from && to ? `${from} → ${to}` : new Date().toLocaleDateString('sq-AL')}
        columns={pdfCols} rows={rows}
        workers={[]}
        orientation="landscape"
      />
    </ReportPage>
  );
}
